// Flat row model for the Clients list (spec §6.1). Built on top of
// buildClientGroups so the env-exact-vs-any-env HubSpot rule stays in one place.
import { buildClientGroups } from "./client-groups";

export interface ClientRow {
  id: string;
  name: string;
  /** Non-disabled environment names, in API order. */
  environments: string[];
  /** One entry per environment (or a single `env: null` entry when there are none). */
  hubspot: { env: string | null; connected: boolean }[];
  active: number;
  paused: number;
}

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface OrgInput { id: string; name: string }
interface InstanceInput {
  id: string;
  organizationId: string;
  connectorType: string;
  environment?: string | null;
  config?: Record<string, unknown>;
}
interface EnvironmentInput { name: string; organizationId: string; disabled?: boolean }
interface PipelineInput { id: string; organizationId?: string; enabled?: boolean; disabled?: boolean }

/** Active unless explicitly turned off, across both shapes the API has used
 *  for that flag — an absent `enabled` must not read as paused. */
function isActive(p: PipelineInput): boolean {
  return p.enabled === true || (p.enabled == null && p.disabled !== true);
}

/**
 * One flat row per client for the Clients list (spec §6.1): its environments,
 * whether HubSpot is connected in each, and its active/paused pipeline counts.
 *
 * Built on `buildClientGroups` so the env-exact-versus-any-env HubSpot rule
 * stays in one place rather than being restated per view.
 */
export function buildClientRows(
  orgs: OrgInput[],
  instances: InstanceInput[],
  environments: EnvironmentInput[],
  pipelines: PipelineInput[],
): ClientRow[] {
  const groups = buildClientGroups(orgs, instances, environments);
  return groups.map((g) => {
    const mine = pipelines.filter((p) => p.organizationId === g.org.id);
    return {
      id: g.org.id,
      name: g.org.name,
      environments: g.subRows.map((s) => s.env).filter((e): e is string => e != null),
      hubspot: g.subRows.map((s) => ({ env: s.env, connected: s.connected })),
      active: mine.filter(isActive).length,
      paused: mine.length - mine.filter(isActive).length,
    };
  });
}

/** The rows matching the list's filters: name substring, exact environment. */
export function filterClientRows(
  rows: ClientRow[],
  filter: { name: string; env: string },
): ClientRow[] {
  const needle = filter.name.trim().toLowerCase();
  return rows.filter((r) => {
    if (needle && !r.name.toLowerCase().includes(needle)) return false;
    if (filter.env && !r.environments.includes(filter.env)) return false;
    return true;
  });
}
